/**
 * TaxReportSection — iter174c · Steuerrechtliche Auswertung
 *
 * Zeitraum-Schnellfilter (Aktueller Monat / Quartal / Jahr / Custom) +
 * Bereichs- und Doktyp-Filter. Live-Load von `/admin/tax/transactions` +
 * `/admin/tax/summary`. Iter G bringt XLSX+ZIP-Export.
 */
import { useEffect, useState } from "react";
import { RefreshCw, Loader2, FileText, Package, TrendingUp, Wallet, HandCoins } from "lucide-react";
import api from "@/lib/api";

const AREAS = [
  { value: "", label: "Alle Bereiche" },
  { value: "MARKETPLACE", label: "Marketplace" },
  { value: "STILLTOUCHEDX", label: "stilltouchedX" },
  { value: "SOCIAL", label: "Social" },
];

const DOCTYPES = [
  { value: "", label: "Alle Dokumente" },
  { value: "ORDER_CONFIRMATION", label: "Bestellübersicht" },
  { value: "SELLER_PAYOUT_STATEMENT", label: "Verkäufer-Abrechnung" },
  { value: "REFUND_RECEIPT", label: "Erstattungsbeleg" },
  { value: "PAYOUT_ADJUSTMENT", label: "Abrechnungskorrektur" },
];

function fmtEUR(cents) {
  if (cents == null) return "—";
  return `${(cents / 100).toFixed(2).replace(".", ",")} €`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
}

function isoStart(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function firstOfMonthISO() {
  const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function firstOfQuarterISO() {
  const d = new Date(); const q = Math.floor(d.getMonth() / 3);
  d.setMonth(q * 3, 1); d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function firstOfYearISO() {
  const d = new Date(); d.setMonth(0, 1); d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export default function TaxReportSection() {
  const [preset, setPreset] = useState("month");
  const [from_, setFrom] = useState(firstOfMonthISO());
  const [to, setTo] = useState(new Date().toISOString());
  const [area, setArea] = useState("");
  const [doctype, setDoctype] = useState("");
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(false);

  const applyPreset = (p) => {
    setPreset(p);
    const now = new Date().toISOString();
    setTo(now);
    if (p === "month") setFrom(firstOfMonthISO());
    else if (p === "quarter") setFrom(firstOfQuarterISO());
    else if (p === "year") setFrom(firstOfYearISO());
    else if (p === "last30") setFrom(isoStart(30));
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/admin/tax/summary", { params: { from: from_, to, area: area || undefined } }),
        api.get("/admin/tax/transactions", { params: { from: from_, to, area: area || undefined, doctype: doctype || undefined } }),
      ]);
      setSummary(s.data);
      setTxs(t.data?.transactions || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="tax-report-section" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-bold text-[#0A0A0A] mr-auto">Steuerrechtliche Auswertung</h2>
        <a
          data-testid="tax-report-export-zip"
          href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/tax/export.zip?from=${encodeURIComponent(from_)}&to=${encodeURIComponent(to)}${area ? `&area=${encodeURIComponent(area)}` : ""}`}
          target="_blank" rel="noreferrer"
          className="h-8 px-3 text-xs flex items-center gap-1.5 bg-[#0A0A0A] text-white hover:bg-[#222] font-semibold"
        >
          Auswertung herunterladen
        </a>
        <button data-testid="tax-report-refresh" onClick={load} disabled={loading}
                className="h-8 px-3 text-xs flex items-center gap-1.5 border border-[#0A0A0A] hover:bg-[#F5F5F5]">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          neu laden
        </button>
      </div>

      {/* Zeitraum-Schnellfilter */}
      <div className="flex flex-wrap gap-2">
        {[
          ["month", "Aktueller Monat"],
          ["quarter", "Aktuelles Quartal"],
          ["year", "Aktuelles Jahr"],
          ["last30", "Letzte 30 Tage"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => applyPreset(k)}
                  data-testid={`tax-preset-${k}`}
                  className={`h-8 px-3 text-[11px] font-semibold border ${
                    preset === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#DDD] text-[#0A0A0A] hover:bg-[#F5F5F5]"}`}>
            {l}
          </button>
        ))}
        <select value={area} onChange={(e) => setArea(e.target.value)} data-testid="tax-filter-area"
                className="h-8 px-2 text-[11px] border border-[#DDD]">
          {AREAS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <select value={doctype} onChange={(e) => setDoctype(e.target.value)} data-testid="tax-filter-doctype"
                className="h-8 px-2 text-[11px] border border-[#DDD]">
          {DOCTYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      </div>

      {/* Summen-Karten */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="tax-summary-cards">
          <SumCard label="Bestellungen" value={summary.order_count} icon={Package} />
          <SumCard label="Umsatz (brutto)" value={fmtEUR(summary.total_gross_cents)} icon={TrendingUp} />
          <SumCard label="Servicegebühren" value={fmtEUR(summary.service_fees_cents)} icon={HandCoins} />
          <SumCard label="Käuferschutz" value={fmtEUR(summary.buyer_protection_cents)} icon={Wallet} />
          <SumCard label="Erstattungen" value={fmtEUR(summary.refunds_cents)} icon={FileText} />
        </div>
      )}

      {/* Transaktionstabelle */}
      <div className="border border-[#E5E5E5] overflow-x-auto" data-testid="tax-tx-table">
        <table className="w-full text-[11px]">
          <thead className="bg-[#F5F5F5] text-[#666] uppercase tracking-wider">
            <tr>
              <th className="text-left px-2 py-1.5">Datum</th>
              <th className="text-left px-2 py-1.5">Bestell-Nr</th>
              <th className="text-left px-2 py-1.5">Doknr</th>
              <th className="text-left px-2 py-1.5">Typ</th>
              <th className="text-left px-2 py-1.5">Empfänger</th>
              <th className="text-left px-2 py-1.5">Bereich</th>
              <th className="text-left px-2 py-1.5">Status</th>
              <th className="text-right px-2 py-1.5">Preis</th>
              <th className="text-right px-2 py-1.5">Fee</th>
            </tr>
          </thead>
          <tbody>
            {txs.length === 0 && (
              <tr><td colSpan="9" className="p-4 text-center text-[#999]">Keine Transaktionen im Zeitraum.</td></tr>
            )}
            {txs.map(t => (
              <tr key={t.document_id} data-testid={`tax-tx-row-${t.document_id}`} className="border-t border-[#F0F0F0]">
                <td className="px-2 py-1.5">{fmtDate(t.created_at)}</td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{t.order_number || "—"}</td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{t.document_number || "—"}</td>
                <td className="px-2 py-1.5">{t.document_type}</td>
                <td className="px-2 py-1.5">{t.recipient_username}</td>
                <td className="px-2 py-1.5">{t.area_scope || "—"}</td>
                <td className="px-2 py-1.5">{t.status || "—"}</td>
                <td className="px-2 py-1.5 text-right">{fmtEUR(t.price_cents)}</td>
                <td className="px-2 py-1.5 text-right">{fmtEUR(t.service_fee_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-[#999]">Live-Datenzeitraum: {fmtDate(from_)} – {fmtDate(to)} · XLSX + ZIP via „Auswertung herunterladen"</p>
    </div>
  );
}

function SumCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-[#E5E5E5] p-3">
      <div className="flex items-center gap-1.5 text-[10px] text-[#666] uppercase tracking-wider">
        <Icon size={12} /> {label}
      </div>
      <p className="text-lg font-bold text-[#0A0A0A] mt-1">{value}</p>
    </div>
  );
}
